import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { fetchAuthMe } from '../services/api';
import { redirectToPortal } from '../utils/portal';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authDisabled, setAuthDisabled] = useState(false);
  const [restoring, setRestoring] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    fetchAuthMe()
      .then((data) => {
        if (!alive) return;
        if (data?.authDisabled) {
          setAuthDisabled(true);
          setUser(data?.user || null);
          setError('');
          return;
        }
        setAuthDisabled(false);
        setUser(data?.user || null);
        setError('');
      })
      .catch((err) => {
        if (!alive) return;
        const status = err?.status;
        if (status === 401 || status === 403) {
          setUser(null);
          setError(err.message || 'กรุณาเข้าสู่ระบบ');
          redirectToPortal();
          return;
        }
        setError(err.message || 'เชื่อมต่อระบบไม่ได้');
      })
      .finally(() => {
        if (alive) setRestoring(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const onExpired = () => {
      setUser(null);
      redirectToPortal();
    };
    window.addEventListener('rollscan:session-expired', onExpired);
    return () => window.removeEventListener('rollscan:session-expired', onExpired);
  }, []);

  const value = useMemo(
    () => ({
      user,
      authDisabled,
      restoring,
      error,
      isAuthenticated: Boolean(user) || authDisabled,
      can: (code) => {
        if (authDisabled) return true;
        const perms = user?.permissions;
        return Array.isArray(perms) && perms.includes(code);
      },
      hasRole: (role) => {
        if (authDisabled) return role === 'developer';
        const roles = user?.roles;
        return Array.isArray(roles) && roles.includes(role);
      },
    }),
    [user, authDisabled, restoring, error]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function AuthGate({ children }) {
  const { restoring, isAuthenticated, error, authDisabled } = useAuth();

  if (restoring) {
    return (
      <div className="auth-gate">
        <div className="auth-gate-card">
          <p className="auth-gate-title">Rollscan</p>
          <p className="auth-gate-msg">กำลังตรวจสอบสิทธิ์…</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="auth-gate">
        <div className="auth-gate-card">
          <p className="auth-gate-title">Rollscan</p>
          <p className="auth-gate-msg">{error || 'กำลังพาไปหน้าเข้าสู่ระบบ…'}</p>
          {!authDisabled && (
            <button type="button" className="auth-gate-btn" onClick={() => redirectToPortal()}>
              ไป LFB Center
            </button>
          )}
        </div>
      </div>
    );
  }

  return children;
}
